<template>
  <b-row>
    <!-- LEFT COLUMN: icon, title, description, and trial footer -->
    <b-col xs="12" md="6">
      <b-card>
        <b-col cols="12">
          <b-row>
            <b-col cols="12" class="heading-icon">
              <div class="integration-icon-circle">
                <IntegrationIcon :icon="model.icon" />
              </div>
              <h4 class="mt-2 mb-0">{{ $translateLabel(model.label) }}</h4>
            </b-col>
            <b-col cols="12">
              <p>
                {{ $t("integrations.payments.description") }}
              </p>
            </b-col>
          </b-row>
        </b-col>

        <StartTrialFooter :integration="model" />
      </b-card>
    </b-col>

    <!-- RIGHT COLUMN: when active === true, show the settings form here -->
    <b-col sm="12" md="6">
      <b-card v-if="active">
        <b-tabs v-model="activeTab">

          <!-- MONRI TAB -->
          <b-tab :title="$t('integrations.payments.tabs.monri')">
            <b-col cols="12" class="pt-3 text-center" v-if="monri.loading">
              <b-spinner label="Loading..."></b-spinner>
            </b-col>

            <b-col cols="12" class="pt-3" v-else-if="$store.getters.getCurrentEnv == 'live'">
              <b-row>
                <b-col cols="12">
                  <b-alert
                    v-if="monri.error"
                    variant="danger"
                    show
                    dismissible
                    @dismissed="monri.error = null"
                  >
                    {{ monri.error }}
                  </b-alert>
                </b-col>

                <b-col cols="12">
                  <b-form @submit.prevent="saveMonriSettings">
                    <b-form-group
                      :label="$t('ecommerce.payment.merchantKey')"
                      label-for="monri-merchant-key"
                    >
                      <b-form-input
                        id="monri-merchant-key"
                        v-model="monri.merchant_key"
                        :placeholder="$t('ecommerce.payment.merchantKeyPlaceholder')"
                        required
                      />
                    </b-form-group>

                    <b-form-group
                      :label="$t('ecommerce.payment.authenticityToken')"
                      label-for="monri-auth-token"
                    >
                      <b-form-input
                        id="monri-auth-token"
                        v-model="monri.authenticity_token"
                        :placeholder="$t('ecommerce.payment.authenticityTokenPlaceholder')"
                        type="password"
                        required
                      />
                    </b-form-group>

                    <b-button type="submit" variant="primary" :disabled="monri.saving">
                      {{ monri.saving ? $t('integrations.payments.monri.saving') : $t('save') }}
                    </b-button>
                  </b-form>
                </b-col>
              </b-row>
            </b-col>

            <b-col cols="12" class="pt-3" v-else-if="!monri.loading">
              <p class="text-muted">
                {{ $t("integrations.payments.monri.settings_disabled") }}
              </p>
            </b-col>

            <b-col cols="12" class="mt-3">
              <p class="text-muted">{{ $t('integrations.payments.monri.integration_disclaimer') }}</p>
            </b-col>
          </b-tab>

          <!-- STRIPE TAB -->
          <b-tab title="Stripe">
            <b-col cols="12" class="pt-3 text-center" v-if="stripe.loading">
              <b-spinner label="Loading..."></b-spinner>
            </b-col>

            <b-col cols="12" class="pt-3" v-else>
              <b-row>
                <b-col cols="12">
                  <b-alert
                    v-if="stripe.error"
                    variant="danger"
                    show
                    dismissible
                    @dismissed="stripe.error = null"
                  >
                    {{ stripe.error }}
                  </b-alert>
                </b-col>

                <b-col cols="12">
                  <b-form @submit.prevent="saveStripeSettings">
                    <b-form-group
                      label="Secret Key"
                      label-for="stripe-secret-key"
                    >
                      <b-form-input
                        id="stripe-secret-key"
                        v-model="stripe.secretKey"
                        :placeholder="$store.getters.getCurrentEnv == 'live' ? 'sk_live_...' : 'sk_test_...'"
                        type="password"
                        required
                      />
                    </b-form-group>

                    <b-button type="submit" variant="primary" :disabled="stripe.saving">
                      {{ stripe.saving ? 'Saving...' : $t('save') }}
                    </b-button>
                  </b-form>
                </b-col>
              </b-row>
            </b-col>
          </b-tab>

          <!-- BRAINTREE TAB -->
          <b-tab title="Braintree">
            <b-col cols="12" class="pt-3 text-center" v-if="braintree.loading">
              <b-spinner label="Loading..."></b-spinner>
            </b-col>

            <b-col cols="12" class="pt-3" v-else>
              <b-row>
                <b-col cols="12">
                  <b-alert
                    v-if="braintree.error"
                    variant="danger"
                    show
                    dismissible
                    @dismissed="braintree.error = null"
                  >
                    {{ braintree.error }}
                  </b-alert>
                </b-col>

                <b-col cols="12">
                  <b-form @submit.prevent="saveBraintreeSettings">
                    <b-form-group
                      label="Merchant ID"
                      label-for="bt-merchant-id"
                    >
                      <b-form-input
                        id="bt-merchant-id"
                        v-model="braintree.merchantId"
                        placeholder="Your Braintree Merchant ID"
                        required
                      />
                    </b-form-group>

                    <b-form-group
                      label="Public Key"
                      label-for="bt-public-key"
                    >
                      <b-form-input
                        id="bt-public-key"
                        v-model="braintree.publicKey"
                        placeholder="Your Braintree Public Key"
                        required
                      />
                    </b-form-group>

                    <b-form-group
                      label="Private Key"
                      label-for="bt-private-key"
                    >
                      <b-form-input
                        id="bt-private-key"
                        v-model="braintree.privateKey"
                        placeholder="Your Braintree Private Key"
                        type="password"
                      />
                    </b-form-group>

                    <b-button type="submit" variant="primary" :disabled="braintree.saving">
                      {{ braintree.saving ? 'Saving...' : $t('save') }}
                    </b-button>
                  </b-form>
                </b-col>
              </b-row>
            </b-col>
          </b-tab>

        </b-tabs>
      </b-card>
    </b-col>
  </b-row>
</template>

<style scoped>
.heading-icon {
  text-align: center;
}

.integration-icon-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: rgba(93, 107, 251, 0.1);
  margin: 0 auto;
}
</style>

<script>
import StartTrialFooter from "../../StartTrialFooter";
import IntegrationIcon from "../../Icon";

export default {
  components: {
    StartTrialFooter,
    IntegrationIcon,
  },
  props: {
    model: {
      type: Object,
      default: () => null,
    },
    active: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeTab: 0,

      monri: {
        merchant_key: "",
        authenticity_token: "",
        loading: false,
        saving: false,
        error: null,
        fetched: false,
      },

      stripe: {
        secretKey: "",
        loading: false,
        saving: false,
        error: null,
        fetched: false,
      },

      braintree: {
        merchantId: "",
        publicKey: "",
        privateKey: "",
        loading: false,
        saving: false,
        error: null,
        fetched: false,
      },

    };
  },
  computed: {
    projectUUID() {
      return this.$store.getters.getCurrentProject.uuid;
    },
  },
  watch: {
    active(newVal) {
      if (newVal) {
        this.getProviderSettings('monri');
      }
    },
    activeTab(tab) {
      if (tab === 1 && !this.stripe.fetched) {
        this.getProviderSettings('stripe');
      } else if (tab === 2 && !this.braintree.fetched) {
        this.getProviderSettings('braintree');
      }
    },
  },
  created() {
    if (this.active) {
      this.getProviderSettings('monri');
    }
  },
  methods: {
    async getProviderSettings(provider) {
      var state = this[provider];
      state.loading = true;
      state.error = null;
      try {
        const resp = await this.$integrationApi.get("/payments/" + provider + "/settings");
        var s = (resp.data && resp.data.settings) || {};
        if (provider === "monri") {
          this.monri.merchant_key = s.merchant_key || "";
          this.monri.authenticity_token = s.authenticity_token || "";
        } else if (provider === "stripe") {
          this.stripe.secretKey = s.secretKey || "";
        } else if (provider === "braintree") {
          this.braintree.merchantId = s.merchantId || "";
          this.braintree.publicKey = s.publicKey || "";
          this.braintree.privateKey = s.privateKey || "";
        }
      } catch (err) {
        console.error("Failed to fetch " + provider + " settings:", err);
        state.error =
          (err.response && err.response.data && err.response.data.message) ||
          err.message ||
          "Unable to load settings.";
      } finally {
        state.loading = false;
        state.fetched = true;
      }
    },

    // ── Monri ──
    async saveMonriSettings() {
      if (!this.monri.merchant_key || !this.monri.authenticity_token) {
        this.monri.error = "Both Merchant Key and Authenticity Token are required.";
        return;
      }
      this.monri.saving = true;
      this.monri.error = null;
      try {
        const resp = await this.$integrationApi.post("/payments/monri/settings", {
          merchant_key: this.monri.merchant_key,
          authenticity_token: this.monri.authenticity_token,
        });
        var s = (resp.data && resp.data.settings) || {};
        this.monri.merchant_key = s.merchant_key || this.monri.merchant_key;
        this.monri.authenticity_token = s.authenticity_token || this.monri.authenticity_token;
        this.$bvToast.toast(this.$t("Saved"), {
          variant: "success",
          title: this.$t("success"),
          autoHideDelay: 3000,
        });
      } catch (err) {
        console.error("Failed to save Monri settings:", err);
        this.monri.error =
          (err.response && err.response.data && err.response.data.message) ||
          err.message ||
          "Failed to save settings.";
      } finally {
        this.monri.saving = false;
      }
    },

    // ── Stripe ──
    async saveStripeSettings() {
      if (!this.stripe.secretKey) {
        this.stripe.error = "Secret Key is required.";
        return;
      }
      this.stripe.saving = true;
      this.stripe.error = null;
      try {
        await this.$integrationApi.post("/payments/stripe/settings", {
          secretKey: this.stripe.secretKey,
        });
        this.$bvToast.toast("Saved", {
          variant: "success",
          title: "Stripe settings saved",
          autoHideDelay: 3000,
        });
      } catch (err) {
        console.error("Failed to save Stripe settings:", err);
        this.stripe.error =
          (err.response && err.response.data && err.response.data.message) ||
          err.message ||
          "Failed to save Stripe settings.";
      } finally {
        this.stripe.saving = false;
      }
    },

    // ── Braintree ──
    async saveBraintreeSettings() {
      if (!this.braintree.merchantId || !this.braintree.publicKey) {
        this.braintree.error = "Merchant ID and Public Key are required.";
        return;
      }
      this.braintree.saving = true;
      this.braintree.error = null;
      try {
        var payload = {
          merchantId: this.braintree.merchantId,
          publicKey: this.braintree.publicKey,
        };
        if (this.braintree.privateKey) {
          payload.privateKey = this.braintree.privateKey;
        }
        await this.$integrationApi.post("/payments/braintree/settings", payload);
        this.$bvToast.toast("Saved", {
          variant: "success",
          title: "Braintree settings saved",
          autoHideDelay: 3000,
        });
      } catch (err) {
        console.error("Failed to save Braintree settings:", err);
        this.braintree.error =
          (err.response && err.response.data && err.response.data.message) ||
          err.message ||
          "Failed to save Braintree settings.";
      } finally {
        this.braintree.saving = false;
      }
    },
  },
};
</script>
